import { Head, useForm, router } from "@inertiajs/react"
import { useState, useRef } from "react"
import { Upload, MapPin, Plus, Pencil, Trash2 } from "lucide-react"
import { CoachLayout } from "./components/CoachLayout"
import { SectionTabs } from "./components/SectionTabs"

const settingsTabs = [
  { label: "Settings", href: "/app/coach/settings" },
  { label: "Tutorials", href: "/app/coach/tutorial_videos" },
]
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Separator } from "@/components/ui/separator"
import { Switch } from "@/components/ui/switch"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { VoiceSelector } from "@/components/podcast/VoiceSelector"
import { ArticleCombobox } from "@/components/admin/ArticleCombobox"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogFooter,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ResponsiveDialogClose,
} from "@/components/ui/responsive-dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { COUNTRIES } from "@/lib/countries"

function LocationDialog({ open, onOpenChange, location = null }) {
  const isEditing = !!location
  const form = useForm({
    city: location?.city || "",
    state: location?.state || "",
    country: location?.country || "US",
    zip_code: location?.zipCode || "",
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    if (isEditing) {
      form.patch(`/app/coach/locations/${location.id}`, {
        preserveScroll: true,
        onSuccess: () => onOpenChange(false),
      })
    } else {
      form.post("/app/coach/locations", {
        preserveScroll: true,
        onSuccess: () => onOpenChange(false),
      })
    }
  }

  const handleClose = (isOpen) => {
    if (!isOpen) {
      form.reset()
      form.clearErrors()
    }
    onOpenChange(isOpen)
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={handleClose}>
      <ResponsiveDialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle className="font-display text-xl">
              {isEditing ? "Edit Location" : "Add Location"}
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              {isEditing ? "Update this location's details." : "Add a location where you coach in-person groups."}
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>

          <div className="space-y-4 py-4 px-4 sm:px-0">
            <div className="space-y-2">
              <Label htmlFor="loc-country">Country</Label>
              <select
                id="loc-country"
                value={form.data.country}
                onChange={(e) => {
                  const newCountry = e.target.value
                  if (newCountry !== form.data.country) {
                    form.setData((prev) => ({
                      ...prev,
                      country: newCountry,
                      state: "",
                      zip_code: "",
                    }))
                  }
                }}
                className="flex h-9 w-full rounded-md border border-zinc-300 bg-white px-3 py-1 text-sm text-zinc-900 shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="loc-city">City</Label>
                <Input
                  id="loc-city"
                  placeholder="Indianapolis"
                  className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                  value={form.data.city}
                  onChange={(e) => form.setData("city", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="loc-state">
                  {form.data.country === "US" ? "State" : "State / Province"}
                </Label>
                <Input
                  id="loc-state"
                  placeholder={form.data.country === "US" ? "IN" : "Province"}
                  {...(form.data.country === "US" ? { maxLength: 2 } : {})}
                  className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                  value={form.data.state}
                  onChange={(e) =>
                    form.setData("state", form.data.country === "US" ? e.target.value.toUpperCase() : e.target.value)
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="loc-zip">
                {form.data.country === "US" ? "Zip Code" : "Postal Code"}
              </Label>
              <Input
                id="loc-zip"
                placeholder={form.data.country === "US" ? "46204" : "Postal code"}
                {...(form.data.country === "US" ? { maxLength: 5 } : {})}
                className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                value={form.data.zip_code}
                onChange={(e) => form.setData("zip_code", e.target.value)}
              />
              {form.errors.zip_code && (
                <p className="text-sm text-red-600">{form.errors.zip_code}</p>
              )}
            </div>
          </div>

          <ResponsiveDialogFooter>
            <ResponsiveDialogClose asChild>
              <Button type="button" variant="outline">Cancel</Button>
            </ResponsiveDialogClose>
            <Button type="submit" variant="gradient" disabled={form.processing}>
              {form.processing ? "Saving..." : isEditing ? "Save Changes" : "Add Location"}
            </Button>
          </ResponsiveDialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}

export default function Settings({ currentUser, account, voices = [] }) {
  const [headshotPreview, setHeadshotPreview] = useState(null)
  const headshotInputRef = useRef(null)
  const [locationDialogOpen, setLocationDialogOpen] = useState(false)
  const [editingLocation, setEditingLocation] = useState(null)
  const [deleteLocation, setDeleteLocation] = useState(null)

  const { data, setData, patch, processing } = useForm({
    name: account.name || "",
    bio: account.bio || "",
    company: account.company || "",
    job_title: account.jobTitle || "",
    writing_guidelines: account.writingGuidelines || "",
    headshot: null,
    favorite_podcast_style: account.favoritePodcastStyle || "",
    favorite_host_voice_id: account.favoriteHostVoiceId?.toString() || "",
    favorite_interviewee_voice_id:
      account.favoriteIntervieweeVoiceId?.toString() || "",
    name_phonetic: account.namePhonetic || "",
    schedule_call_url: account.scheduleCallUrl || "",
    profile_article_id: account.profileArticleId?.toString() || "",
    is_virtual: account.isVirtual || false,
    is_international: account.isInternational || false,
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    patch("/app/coach/settings", { preserveScroll: true, forceFormData: true })
  }

  const handleHeadshotChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setData("headshot", file)
      setHeadshotPreview(URL.createObjectURL(file))
    }
  }

  const currentHeadshotUrl = headshotPreview || account.headshotUrl

  return (
    <CoachLayout currentUser={currentUser}>
      <Head title="Settings - Kingdom Factor" />

      <div className="space-y-6 max-w-2xl">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">Settings</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Manage your profile and AI writing preferences.
          </p>
        </div>

        <SectionTabs tabs={settingsTabs} />

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Profile Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Profile</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Headshot Upload */}
              <div data-tour="profile-photo" className="flex items-center gap-6">
                <div className="relative w-24 h-24 rounded-full overflow-hidden bg-zinc-100 flex items-center justify-center flex-shrink-0 border-2 border-zinc-200">
                  {currentHeadshotUrl ? (
                    <img
                      src={currentHeadshotUrl}
                      alt="Profile photo"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-2xl font-display text-zinc-400">
                      {(account.name || "?").charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="space-y-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => headshotInputRef.current?.click()}
                  >
                    <Upload className="h-4 w-4" />
                    Upload Photo
                  </Button>
                  <input
                    ref={headshotInputRef}
                    type="file"
                    accept="image/webp,image/png,image/jpeg"
                    onChange={handleHeadshotChange}
                    className="hidden"
                  />
                  <p className="text-xs text-zinc-500">
                    Square photo recommended (400x400px). Max 5MB.
                  </p>
                </div>
              </div>

              <Separator />

              <div data-tour="profile-name" className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={data.name}
                    onChange={(e) => setData("name", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company">Company</Label>
                  <Input
                    id="company"
                    value={data.company}
                    onChange={(e) => setData("company", e.target.value)}
                  />
                </div>
              </div>
              <div data-tour="profile-company-job" className="space-y-2">
                <Label htmlFor="job_title">Job Title</Label>
                <Input
                  id="job_title"
                  value={data.job_title}
                  onChange={(e) => setData("job_title", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bio">Bio</Label>
                <Textarea
                  id="bio"
                  value={data.bio}
                  onChange={(e) => setData("bio", e.target.value)}
                  rows={3}
                  placeholder="A short bio for your author profile"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="schedule_call_url">Schedule a Call Link</Label>
                <Input
                  id="schedule_call_url"
                  value={data.schedule_call_url}
                  onChange={(e) => setData("schedule_call_url", e.target.value)}
                  placeholder="https://your-calendar-link.com"
                />
                <p className="text-xs text-zinc-500">
                  Your HighLevel calendar link, Calendly, or any scheduling URL. This appears on your articles and coach profile.
                </p>
              </div>
              {account.profileArticleOptions?.length > 0 && (
                <div className="space-y-2">
                  <Label>Profile Article</Label>
                  <ArticleCombobox
                    articles={account.profileArticleOptions}
                    value={data.profile_article_id}
                    onSelect={(id) => setData("profile_article_id", id)}
                    placeholder="Select a profile article"
                    emptyLabel="No profile article"
                  />
                  <p className="text-xs text-zinc-500">
                    Select your self-authored profile article. This will be linked from the coaches directory.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Locations & Availability */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">Locations & Availability</CardTitle>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditingLocation(null)
                  setLocationDialogOpen(true)
                }}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Location
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {account.locations?.length > 0 ? (
                <div className="space-y-2">
                  {account.locations.map((loc) => (
                    <div
                      key={loc.id}
                      className="flex items-center justify-between rounded-lg border border-zinc-200 p-3"
                    >
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-zinc-400 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-zinc-900">
                            {[loc.city, loc.state].filter(Boolean).join(", ") || "No city/state"}
                            {loc.country && loc.country !== "US" && ` (${loc.country})`}
                          </p>
                          <p className="text-xs text-zinc-500">
                            {loc.zipCode || "No zip code"}
                            {loc.isGeocoded && (
                              <span className="text-green-600 ml-2">Geocoded</span>
                            )}
                            {loc.zipCode && !loc.isGeocoded && (
                              <span className="text-amber-600 ml-2">Not geocoded</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingLocation(loc)
                            setLocationDialogOpen(true)
                          }}
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteLocation(loc)}
                          title="Delete"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-zinc-400">
                  <MapPin className="h-8 w-8 mx-auto mb-2 text-zinc-300" />
                  <p className="text-sm font-medium text-zinc-500">No locations added</p>
                  <p className="text-xs">Add your coaching locations so nearby prospects can find you.</p>
                </div>
              )}

              <Separator />

              <div className="flex items-center justify-between rounded-lg border border-zinc-200 p-3">
                <div className="space-y-0.5">
                  <Label htmlFor="is_virtual" className="cursor-pointer">
                    Virtual Coach
                  </Label>
                  <p className="text-xs text-zinc-500">
                    Available as fallback for prospects outside local range
                  </p>
                </div>
                <Switch
                  id="is_virtual"
                  checked={data.is_virtual}
                  onCheckedChange={(checked) => setData("is_virtual", checked)}
                />
              </div>

              <div className="flex items-center justify-between rounded-lg border border-zinc-200 p-3">
                <div className="space-y-0.5">
                  <Label htmlFor="is_international" className="cursor-pointer">
                    International Coach
                  </Label>
                  <p className="text-xs text-zinc-500">
                    Receives leads from prospects outside the United States
                  </p>
                </div>
                <Switch
                  id="is_international"
                  checked={data.is_international}
                  onCheckedChange={(checked) => setData("is_international", checked)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Writing Guidelines */}
          <Card data-tour="writing-guidelines">
            <CardHeader>
              <CardTitle className="text-base">
                AI Writing Guidelines
              </CardTitle>
              <p className="text-sm text-zinc-500">
                These guidelines help the AI assistant match your voice and style
                when editing articles. They're optional — the AI already uses KF
                brand voice by default.
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label htmlFor="writing_guidelines">
                  Your Writing Style Preferences
                </Label>
                <Textarea
                  id="writing_guidelines"
                  value={data.writing_guidelines}
                  onChange={(e) =>
                    setData("writing_guidelines", e.target.value)
                  }
                  rows={6}
                  placeholder="e.g., Always use active voice. Keep paragraphs short (2-3 sentences). Reference scripture naturally, not preachy. Use personal anecdotes to illustrate points."
                />
                <p className="text-xs text-zinc-500">
                  {data.writing_guidelines?.length || 0}/5,000 characters
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Podcast Preferences */}
          <Card data-tour="podcast-defaults">
            <CardHeader>
              <CardTitle className="text-base">
                Podcast Defaults
              </CardTitle>
              <p className="text-sm text-zinc-500">
                Set your preferred podcast style and voices. These will be
                pre-filled when you generate a podcast for any article.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Preferred Podcast Style</Label>
                <Select
                  value={data.favorite_podcast_style}
                  onValueChange={(value) =>
                    setData("favorite_podcast_style", value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select style" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="interview">Interview</SelectItem>
                    <SelectItem value="deep_dive">Deep Dive</SelectItem>
                    <SelectItem value="solo">Solo Narration</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div data-tour="pronunciation-tool" className="space-y-2">
                <Label htmlFor="name_phonetic">Name Pronunciation</Label>
                <Input
                  id="name_phonetic"
                  value={data.name_phonetic}
                  onChange={(e) => setData("name_phonetic", e.target.value)}
                  placeholder="e.g., KLEH-ven-jer"
                />
                <p className="text-xs text-zinc-500">
                  Spell your name the way it sounds so the AI narrator pronounces
                  it correctly.{" "}
                  <a
                    href="https://www.howtopronounce.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-kf-red underline"
                  >
                    Need help? Try this tool
                  </a>
                </p>
              </div>

              <div data-tour="voice-selector-host">
                <VoiceSelector
                  label="Preferred Host Voice"
                  voices={voices}
                  value={data.favorite_host_voice_id}
                  onChange={(id) => setData("favorite_host_voice_id", id)}
                />
              </div>

              {data.favorite_podcast_style !== "solo" && (
                <div data-tour="voice-selector-interviewee">
                  <VoiceSelector
                    label="Preferred Interviewee / Second Host Voice"
                    voices={voices}
                    value={data.favorite_interviewee_voice_id}
                    onChange={(id) =>
                      setData("favorite_interviewee_voice_id", id)
                    }
                  />
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button variant="gradient" type="submit" disabled={processing}>
              {processing ? "Saving..." : "Save Settings"}
            </Button>
          </div>
        </form>
      </div>

      {/* Location Dialog */}
      <LocationDialog
        key={editingLocation?.id || "new"}
        open={locationDialogOpen}
        onOpenChange={setLocationDialogOpen}
        location={editingLocation}
      />

      {/* Delete Location Confirmation */}
      <AlertDialog
        open={!!deleteLocation}
        onOpenChange={(open) => !open && setDeleteLocation(null)}
      >
        <AlertDialogContent className="bg-white border-zinc-200">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Location</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove{" "}
              <strong>
                {deleteLocation && [deleteLocation.city, deleteLocation.state].filter(Boolean).join(", ")}
              </strong>
              ? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                router.delete(`/app/coach/locations/${deleteLocation.id}`, {
                  preserveScroll: true,
                  onSuccess: () => setDeleteLocation(null),
                })
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </CoachLayout>
  )
}
